import moment from 'moment';
import * as Sentry from '@sentry/browser';

import Program from '../../../../../../Model/Program';
import Track from '../../../../../../Model/Track';
import Workout from '../../../../../../Model/Workout';
import UserActivityProfile from '../../../../../Model/UserActivityProfile';
import { ScheduledAssignmentType } from '../../../../../../Model/ScheduledAssignment';
import { DateFormat } from '../../../../../../utils/date';

/**
 * A workout a PENDING scheduled assignment is going to write when it fires,
 * shown on the calendar as an editable draft (S2-7706).
 *
 * The dates are predicted here rather than read from the assignment: only the
 * WORKOUT type stores its dates up front — PROGRAM and TRACK resolve them at
 * fire time. So this mirrors the server-side scheduling in
 * `functions-22/src/workout/utils/track-assignment.ts`; the two must stay in
 * step. A mismatch is not silent: an edit saved against a date the processor
 * doesn't end up using is reported to Sentry as a dropped override, and the
 * assignment is written from the workout's own content for that date.
 *
 * @typedef {Object} ScheduledDraft
 * @property {string} id - Unique id of the draft, for the calendar event.
 * @property {string} scheduledAssignmentId - The assignment that will write it.
 * @property {string} date - The `YYYY-MM-DD` date it will land on.
 * @property {string} workoutId - The source workout.
 * @property {Object} workoutContent - The content to assign: the coach's edit
 *   when they have edited this date, otherwise the source workout's own content.
 * @property {boolean} isEdited - Whether the coach has already edited this date.
 */

const DRAFT_ID_PREFIX = 'scheduledDraft';

/**
 * Formats a date as the `YYYY-MM-DD` key the server stores workout overrides
 * under (see `workoutOverrideKey` in the server's `workout-overrides.ts`).
 *
 * @param {Date|moment.Moment|string} date - The date to format.
 * @returns {string} The date key.
 */
const toDateKey = (date) => moment.utc(date).format(DateFormat.DEFAULT_DATE_FORMAT);

/**
 * Builds the dates for the given preferred workout days, starting from the week
 * of `weekStart`. Mirrors the server's `buildPreferredDayDates`.
 *
 * @param {moment.Moment} weekStart - Monday of the target week.
 * @param {Array<number>} preferredDays - Monday-based day indices (0 = Monday).
 * @param {number} count - How many dates to produce, spanning weeks as needed.
 * @returns {Array<string>} The date keys, in chronological order.
 */
const buildPreferredDayDates = (weekStart, preferredDays, count) => {
  const sortedDays = [...preferredDays].sort((dayA, dayB) => dayA - dayB);
  const dates = [];

  while (dates.length < count) {
    const weekOffset = Math.floor(dates.length / sortedDays.length);
    // The stored convention is Monday-based, so the day index is already the offset from weekStart.
    const dayIndex = sortedDays[dates.length % sortedDays.length];
    dates.push(toDateKey(moment.utc(weekStart).add((weekOffset * 7) + dayIndex, 'days')));
  }

  return dates;
};

/**
 * Builds the schedule a program assignment produces. Mirrors the server's
 * `buildProgramSchedule`: preferred days place the program's workouts on the
 * client's chosen weekdays, while the legacy path lays every program entry —
 * rest-day gaps included — on consecutive days.
 *
 * @param {Object} params - The parameters.
 * @param {Array<Object>} params.programWorkouts - The program's `workouts` entries.
 * @param {Object} params.workoutContentById - Source workout content, keyed by workout id.
 * @param {string} params.startDate - The `YYYY-MM-DD` date the program starts on.
 * @param {number} [params.repetitions] - How many times the program repeats.
 * @param {Array<number>} [params.preferredWorkoutDays] - The client's preferred days.
 * @param {string} [params.skipBefore] - Drops workouts landing before this date key.
 * @returns {Array<Object>} `{ date, workoutId }` entries in chronological order.
 */
const buildProgramSchedule = ({
  programWorkouts,
  workoutContentById,
  startDate,
  repetitions = 1,
  preferredWorkoutDays,
  skipBefore,
}) => {
  // Rest days and workouts that no longer exist take no slot of their own on the
  // preferred-day path, so they are filtered out before the days are handed out.
  const validWorkoutIds = programWorkouts
    .map(({ workoutRef }) => workoutRef)
    .filter((workoutRef) => workoutRef && workoutContentById[workoutRef]);

  if (validWorkoutIds.length === 0) {
    return [];
  }

  const usePreferredDays = preferredWorkoutDays?.length === validWorkoutIds.length;
  const schedule = [];

  if (usePreferredDays) {
    const totalWorkouts = validWorkoutIds.length * repetitions;
    const weekStart = moment.utc(startDate).startOf('isoWeek');
    const dates = buildPreferredDayDates(weekStart, preferredWorkoutDays, totalWorkouts);

    for (let index = 0; index < totalWorkouts; index += 1) {
      schedule.push({
        date: dates[index],
        workoutId: validWorkoutIds[index % validWorkoutIds.length],
      });
    }
  } else {
    const totalDays = programWorkouts.length * repetitions;

    for (let index = 0; index < totalDays; index += 1) {
      const { workoutRef } = programWorkouts[index % programWorkouts.length];
      if (workoutRef && workoutContentById[workoutRef]) {
        schedule.push({
          date: toDateKey(moment.utc(startDate).add(index, 'days')),
          workoutId: workoutRef,
        });
      }
    }
  }

  return skipBefore
    ? schedule.filter(({ date }) => date >= skipBefore)
    : schedule;
};

/**
 * Returns the consecutive run of track programs that share the programRef at
 * `startIndex` — the repeat group the track editor encodes. Mirrors the server's
 * `getConsecutiveGroup`.
 *
 * @param {Array<Object>} programs - The track's programs.
 * @param {number} startIndex - Where the group starts.
 * @returns {Object} `{ programRef, groupSize }` of the group.
 */
const getConsecutiveGroup = (programs, startIndex) => {
  const { programRef } = programs[startIndex];
  let endIndex = startIndex;

  while (endIndex + 1 < programs.length && programs[endIndex + 1].programRef === programRef) {
    endIndex += 1;
  }

  return { programRef, groupSize: (endIndex - startIndex) + 1 };
};

/**
 * Loads a program and the content of every workout it references.
 *
 * @param {string} programId - The program to load.
 * @returns {Promise<Object|void>} `{ programWorkouts, workoutContentById }`, or
 *   null when the program is gone or holds no workouts.
 */
const loadProgramContent = async (programId) => {
  const programDoc = await Program.getProgramById(programId);
  const programWorkouts = programDoc?.workouts || [];

  if (programWorkouts.length === 0) {
    return null;
  }

  const workoutIds = [...new Set(programWorkouts.map(({ workoutRef }) => workoutRef).filter(Boolean))];
  const workoutDocs = await Promise.all(workoutIds.map((workoutId) => Workout.getWorkoutById(workoutId)));

  const workoutContentById = workoutDocs.reduce((contentById, workoutDoc, index) => (
    workoutDoc?.exists
      ? { ...contentById, [workoutIds[index]]: workoutDoc.data }
      : contentById
  ), {});

  return { programWorkouts, workoutContentById };
};

/**
 * Predicts the schedule of a PENDING scheduled assignment, per its type.
 *
 * @param {ScheduledAssignment} scheduledAssignmentDoc - The pending assignment.
 * @returns {Promise<Object>} `{ schedule, workoutContentById }`, where schedule
 *   holds `{ date, workoutId }` entries.
 */
const predictSchedule = async (scheduledAssignmentDoc) => {
  const {
    type,
    payload,
    userId,
    scheduledFor,
  } = scheduledAssignmentDoc;
  const empty = { schedule: [], workoutContentById: {} };

  if (!payload) {
    return empty;
  }

  if (type === ScheduledAssignmentType.WORKOUT) {
    const { workoutId, dates } = payload;

    if (!workoutId || !dates?.length) {
      return empty;
    }

    const workoutDoc = await Workout.getWorkoutById(workoutId);

    if (!workoutDoc.exists) {
      return empty;
    }

    return {
      schedule: dates.map((date) => ({ date: toDateKey(date), workoutId })),
      workoutContentById: { [workoutId]: workoutDoc.data },
    };
  }

  if (type === ScheduledAssignmentType.PROGRAM) {
    const { programId, startDate, repetitions } = payload;

    if (!programId || !startDate) {
      return empty;
    }

    const programContent = await loadProgramContent(programId);

    if (!programContent) {
      return empty;
    }

    // Scheduled programs are assigned without preferred days, on consecutive days
    // from the start date the coach picked.
    return {
      schedule: buildProgramSchedule({
        ...programContent,
        startDate: toDateKey(startDate),
        repetitions,
      }),
      workoutContentById: programContent.workoutContentById,
    };
  }

  if (type === ScheduledAssignmentType.TRACK) {
    const { trackId, startingProgramIndex = 0, startDate } = payload;

    // An AUTO track assignment picks its track only when it fires, so there is
    // nothing to preview — and nothing to edit — until then.
    if (!trackId) {
      return empty;
    }

    const trackDoc = await Track.getTrackById(trackId);
    const programs = trackDoc?.programs || [];

    if (programs.length === 0 || startingProgramIndex >= programs.length) {
      return empty;
    }

    const { programRef, groupSize } = getConsecutiveGroup(programs, startingProgramIndex);
    const programContent = await loadProgramContent(programRef);

    if (!programContent) {
      return empty;
    }

    // The processor resolves "today" when the assignment fires, so the send date
    // — not the current date — is what the prediction has to reckon with.
    const fireDate = toDateKey(scheduledFor?.toDate?.() || moment.utc());
    const anchorDate = startDate ? toDateKey(startDate) : fireDate;
    const activityProfileDoc = await UserActivityProfile.getById(userId);
    const preferredWorkoutDays = activityProfileDoc?.preferredWorkoutDays;

    return {
      schedule: buildProgramSchedule({
        ...programContent,
        // Track programs are anchored to the Monday of the start date's week so
        // their workouts land on the intended weekdays.
        startDate: toDateKey(moment.utc(anchorDate).startOf('isoWeek')),
        repetitions: groupSize,
        preferredWorkoutDays: preferredWorkoutDays?.length === trackDoc.workoutsPerWeek
          ? preferredWorkoutDays
          : undefined,
        skipBefore: anchorDate > fireDate ? anchorDate : fireDate,
      }),
      workoutContentById: programContent.workoutContentById,
    };
  }

  return empty;
};

/**
 * Picks the content a draft shows: the coach's edit when it was made against this
 * workout, otherwise the source workout's own.
 *
 * An override belongs to the workout it was edited against, not to the date alone:
 * a schedule that shifts between the edit and now can leave a different workout in
 * the edited slot. Mirrors the processor's `resolveWorkoutContent` — the stale edit
 * is ignored on both sides rather than shown here and dropped there.
 *
 * @param {Object} [override] - The stored override for the draft's date, if any.
 * @param {string} workoutId - The workout the schedule puts on that date.
 * @param {Object} [sourceContent] - The source workout's own content.
 * @returns {Object} `{ workoutContent, isEdited }` for the draft.
 */
const resolveDraftContent = (override, workoutId, sourceContent) => {
  const isEdited = override?.workoutId === workoutId;

  return {
    workoutContent: isEdited ? override.workout : sourceContent,
    isEdited,
  };
};

/**
 * Builds the editable drafts of one PENDING scheduled assignment.
 *
 * @param {ScheduledAssignment} scheduledAssignmentDoc - The pending assignment.
 * @returns {Promise<Array<ScheduledDraft>>} Its drafts, in chronological order.
 */
const getScheduledAssignmentDrafts = async (scheduledAssignmentDoc) => {
  const { schedule, workoutContentById } = await predictSchedule(scheduledAssignmentDoc);
  const { workoutOverrides } = scheduledAssignmentDoc;

  return schedule.map(({ date, workoutId }) => {
    const { workoutContent, isEdited } = resolveDraftContent(
      workoutOverrides[date],
      workoutId,
      workoutContentById[workoutId],
    );

    return {
      id: `${DRAFT_ID_PREFIX}_${scheduledAssignmentDoc.id}_${date}`,
      scheduledAssignmentId: scheduledAssignmentDoc.id,
      date,
      workoutId,
      workoutContent,
      isEdited,
    };
  });
};

/**
 * Builds the editable drafts of every given PENDING scheduled assignment.
 * A draft is best-effort: an assignment whose content cannot be read is left
 * out rather than failing the whole calendar.
 *
 * @param {Array<ScheduledAssignment>} scheduledAssignmentDocs - The pending assignments.
 * @returns {Promise<Array<ScheduledDraft>>} The drafts of all of them.
 */
const getScheduledDrafts = async (scheduledAssignmentDocs) => {
  const draftsPerAssignment = await Promise.all(
    /*
      Reported rather than swallowed: without this an assignment whose content cannot be
      read leaves the coach with a banner and no drafts, and no way to tell why — while
      the server half of the feature does report its dropped overrides. A prediction that
      cannot be read at all should be just as visible.
    */
    scheduledAssignmentDocs.map((doc) => getScheduledAssignmentDrafts(doc).catch((error) => {
      Sentry.captureException(error, {
        extra: {
          scheduledAssignmentId: doc.id,
          type: doc.type,
        },
      });
      return [];
    })),
  );

  return draftsPerAssignment.flat();
};

export default getScheduledDrafts;
